import type {
  BillingStatus,
  CloudBoardSettings,
  CloudBoardUsage,
  CloudEntitlements,
  CloudTrialState,
  CloudZipRestoreTrialState,
  EffectiveCloudPlan,
} from "@board-preserve/contracts";

interface PlanAndUsagePanelProps {
  boardName: string;
  entitlements: CloudEntitlements;
  onComparePlans: () => void;
  onOpenBackup: () => void;
  settings: CloudBoardSettings;
  trial: CloudTrialState;
  usage?: CloudBoardUsage | undefined;
  workspaceName: string;
  zipRestoreTrial?: CloudZipRestoreTrialState | undefined;
}

interface PlanComparisonPanelProps {
  currentPlan?: EffectiveCloudPlan | undefined;
  onStartTrial: () => void;
  workspaceName?: string | undefined;
}

const Check = () => (
  <span className="plan-check" aria-hidden="true">
    ✓
  </span>
);

export function planName(plan: EffectiveCloudPlan): string {
  return plan === "pro_extended"
    ? "Pro Extended"
    : plan === "pro"
      ? "Pro"
      : "Trial";
}

export function billingStatusLabel(status: BillingStatus): string {
  return {
    active: "Active",
    canceled: "Cancelled",
    not_subscribed: "Not subscribed",
    past_due: "Payment needs attention",
    paused: "Paused",
    trialing: "Trial access",
  }[status];
}

export function storageAllowanceLabel(bytes: number): string {
  if (bytes < 1_000_000_000) return `${Math.round(bytes / 1_000_000)} MB`;
  const gigabytes = bytes / 1_000_000_000;
  return `${Number.isInteger(gigabytes) ? gigabytes : gigabytes.toFixed(1)} GB`;
}

export function storageUsageLabel(bytes: number): string {
  if (bytes === 0) return "0 MB";
  if (bytes < 1_000_000) return `${Math.max(1, Math.round(bytes / 1_000))} KB`;
  if (bytes < 1_000_000_000) {
    return `${(bytes / 1_000_000).toFixed(bytes < 10_000_000 ? 1 : 0)} MB`;
  }
  return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
}

export function scheduleSummary(settings: CloudBoardSettings): string {
  if (settings.scheduleTestMinutes === 10) return "Every 10 minutes (test)";
  if (settings.schedule.frequency === "off") return "Manual only";
  if (settings.schedule.frequency === "six_hourly") return "Every 6 hours";
  if (settings.schedule.frequency === "twelve_hourly") return "Every 12 hours";
  if (settings.schedule.frequency === "daily") return "Daily";
  return `Weekly on ${settings.schedule.weekday}`;
}

export function retentionSummary(settings: CloudBoardSettings): string {
  return settings.retentionTestMinutes === 5
    ? "5 minutes (test)"
    : `${settings.retentionDays} day${settings.retentionDays === 1 ? "" : "s"}`;
}

function trialBackupAllowance(trial: CloudTrialState): string {
  if (trial.status === "available") return "1 of 1 left";
  if (trial.status === "reserved") return "Backup in progress";
  return "Used";
}

function UsageMeter({
  label,
  limit,
  value,
}: {
  label: string;
  limit: number;
  value: number | null;
}) {
  const percent =
    value === null ? 0 : Math.min(100, Math.max(0, (value / limit) * 100));
  return (
    <div className="workspace-storage-meter">
      <div className="usage-row-heading">
        <span>{label}</span>
        <strong>
          {value === null ? "Calculating…" : storageUsageLabel(value)} of{" "}
          {storageAllowanceLabel(limit)}
        </strong>
      </div>
      <span
        aria-label={`${label} used`}
        aria-valuemax={limit}
        aria-valuemin={0}
        aria-valuenow={value ?? undefined}
        className="usage-track"
        role="progressbar"
      >
        <span style={{ width: `${percent}%` }} />
      </span>
      <p>
        Shared by every protected board in this Workspace. No other customer
        uses it.
      </p>
    </div>
  );
}

function PaidPlanDetails({
  plan,
  settings,
}: {
  plan: "pro" | "pro_extended";
  settings: CloudBoardSettings;
}) {
  const extended = plan === "pro_extended";
  return (
    <section className="plan-benefit-card">
      <h3>{extended ? "Included with Pro Extended" : "Pro Extended adds"}</h3>
      <div className="plan-benefit-list">
        {extended ? (
          <>
            <p>
              <Check />
              Backups every 6 or 12 hours, daily, or weekly
            </p>
            <p>
              <Check />
              Keep each backup for up to 30 days
            </p>
            <p>
              <Check />
              100 GB of private Workspace storage
            </p>
          </>
        ) : (
          <>
            <p>Backups every 6 or 12 hours, as well as daily and weekly</p>
            <p>Retention up to 30 days instead of 7</p>
            <p>100 GB Workspace storage instead of 25 GB</p>
          </>
        )}
      </div>
      {extended ? (
        <div className="current-board-policy">
          <span>This board</span>
          <strong>
            {scheduleSummary(settings)} · {retentionSummary(settings)} retention
          </strong>
        </div>
      ) : null}
    </section>
  );
}

export function PlanAndUsagePanel({
  boardName,
  entitlements,
  onComparePlans,
  onOpenBackup,
  settings,
  trial,
  usage,
  workspaceName,
  zipRestoreTrial,
}: PlanAndUsagePanelProps) {
  const plan = entitlements.effectivePlan;
  const paid = plan !== "free";

  return (
    <section className="plan-and-usage" id="cloud-plan-panel">
      {entitlements.billingAttention ? (
        <div className="plan-attention" role="status">
          <strong>Payment details need attention</strong>
          <p>
            Existing backups remain available until their expiry dates. New paid
            work may pause if the subscription cannot renew.
          </p>
        </div>
      ) : null}

      <section className="workspace-usage-card" aria-label="Workspace usage">
        <UsageMeter
          label="Workspace storage"
          limit={entitlements.workspaceStorageLimitBytes}
          value={usage?.workspaceStorageBytes ?? null}
        />
        <div className="usage-fact-row">
          <span>Protected boards</span>
          <strong>
            {usage ? usage.protectedBoardCount : "—"} of{" "}
            {entitlements.maxProtectedBoards}
          </strong>
        </div>
        {!paid ? (
          <div className="trial-allowance-list">
            <div>
              <span>Free cloud backup</span>
              <strong>{trialBackupAllowance(trial)}</strong>
            </div>
            <div>
              <span>ZIP recovery</span>
              <strong>
                {zipRestoreTrial?.status === "used" ? "Used" : "1 of 1 left"}
              </strong>
            </div>
            <div>
              <span>Local exports</span>
              <strong>Unlimited</strong>
            </div>
          </div>
        ) : null}
      </section>

      {paid ? (
        <>
          <PaidPlanDetails plan={plan} settings={settings} />
          <div className="plan-management-row">
            <button onClick={onComparePlans} type="button">
              Manage plan
            </button>
            <span>Review plan options for {workspaceName}.</span>
          </div>
          <p className="plan-vat-note">
            Prices shown on the billing page include UK VAT. Plan access updates
            here only after payment is confirmed.
          </p>
        </>
      ) : (
        <>
          <section className="trial-upgrade-card">
            <h3>Upgrade options</h3>
            <div>
              <div>
                <strong>Pro</strong>
                <p>
                  Daily or weekly backups, 7-day history, 5 boards, 25 GB,
                  comparison and restore.
                </p>
              </div>
              <span className="plan-inline-price">
                <strong>£15</strong>
                <small>a month</small>
              </span>
            </div>
            <div>
              <div>
                <strong>Pro Extended</strong>
                <p>
                  6 or 12-hour backups, 30-day history and 100 GB of storage.
                </p>
              </div>
              <span className="plan-inline-price">
                <strong>£29</strong>
                <small>a month</small>
              </span>
            </div>
            <div>
              <div>
                <strong>50 GB storage pack</strong>
                <p>
                  Add private storage to a paid Workspace. It is reserved for
                  that Workspace and is never shared with another customer.
                </p>
              </div>
              <span className="plan-inline-price">
                <strong>£5</strong>
                <small>a month</small>
              </span>
            </div>
          </section>
          <div className="plan-management-row">
            <button onClick={onComparePlans} type="button">
              Compare all plans
            </button>
            <span>See the full comparison, including storage packs.</span>
          </div>
        </>
      )}

      <div className="plan-context-actions">
        <span>{boardName}</span>
        <button
          className="secondary-action"
          onClick={onOpenBackup}
          type="button"
        >
          Backup settings
        </button>
      </div>
    </section>
  );
}

function PlanChoice({
  current,
  description,
  extended = false,
  features,
  name,
  price,
}: {
  current: boolean;
  description: string;
  extended?: boolean;
  features: string[];
  name: string;
  price: string;
}) {
  return (
    <article
      className={`plan-choice-card${extended ? "" : " plan-choice-featured"}`}
    >
      <header>
        <div>
          <span className="plan-choice-name">
            <strong>{name}</strong>
            {!extended ? <small>Most boards</small> : null}
          </span>
          <p>{description}</p>
        </div>
        <span className="plan-choice-price">
          <strong>{price}</strong>
          <small>a month, inc. VAT</small>
        </span>
      </header>
      <div className="plan-choice-body">
        {extended ? (
          <p className="plan-choice-intro">Everything in Pro, plus:</p>
        ) : null}
        <ul>
          {features.map((feature) => (
            <li key={feature}>
              <Check />
              {feature}
            </li>
          ))}
        </ul>
        <button
          className={extended ? "secondary-action" : ""}
          disabled
          type="button"
        >
          {current ? "Your current plan" : `Choose ${name}`}
        </button>
      </div>
    </article>
  );
}

export function PlanComparisonPanel({
  currentPlan = "free",
  onStartTrial,
  workspaceName = "this Workspace",
}: PlanComparisonPanelProps) {
  return (
    <section className="plan-comparison" aria-labelledby="cloud-title">
      <section className="current-plan-summary">
        <div>
          <strong>{planName(currentPlan)} · your current plan</strong>
          <span>
            {currentPlan === "free"
              ? "One backup per Workspace, kept for 48 hours."
              : currentPlan === "pro"
                ? "Daily or weekly backups, 5 protected boards and 25 GB of storage."
                : "Frequent backups, 30-day history and 100 GB of storage."}
          </span>
        </div>
      </section>
      <PlanChoice
        current={currentPlan === "pro"}
        description="For a board you would not want to rebuild by hand."
        features={[
          "Daily or weekly automatic backups",
          "Keep each backup for 1, 3 or 7 days",
          "5 protected boards and 25 GB Workspace storage",
          "Compare backups, recovery boards and restore",
        ]}
        name="Pro"
        price="£15"
      />
      <PlanChoice
        current={currentPlan === "pro_extended"}
        description="For boards that change throughout the day."
        extended
        features={[
          "Backups every 6 or 12 hours",
          "Keep each backup for up to 30 days",
          "100 GB Workspace storage",
        ]}
        name="Pro Extended"
        price="£29"
      />
      {currentPlan === "free" ? (
        <button
          className="trial-return-action"
          onClick={onStartTrial}
          type="button"
        >
          Continue with Trial
        </button>
      ) : null}
      <footer className="plan-comparison-footer">
        <p>Need more storage later? Add 50 GB packs to either paid plan.</p>
        <p>
          Plans cover every protected board in {workspaceName}. Local exports
          remain unlimited.
        </p>
      </footer>
    </section>
  );
}
